import React, { useState, useEffect, useRef } from "react";

import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { useNavigate } from "react-router-dom";

import { getProfileAvatar, getCurrentProfile } from "../utils/profileUtils";
import webPlayerStorage from "../utils/webPlayerStorage";

import LunaAIOverlay from "./LunaAIOverlay";
import UserInfoOverlay from "./UserInfoOverlay";

const LeftMenu = ({
  profile,
  playlist,
  activeMenuItem = "home",
  isDarkMode = true,
}) => {
  const navigate = useNavigate();
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [hoveredMenuItem, setHoveredMenuItem] = useState(null);
  const [activeItem, setActiveItem] = useState(activeMenuItem);
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [userAvatar, setUserAvatar] = useState(null);
  const [overlayKey, setOverlayKey] = useState(0);
  const [showLunaAI, setShowLunaAI] = useState(false);
  const [lunaAnimation, setLunaAnimation] = useState(null);
  const [isLunaHovered, setIsLunaHovered] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const sidebarRef = useRef(null);

  // Check if user is authenticated
  const isAuthenticated = !!webPlayerStorage.getToken();

  // Reopen the user-info overlay when returning from the avatars page
  useEffect(() => {
    if (sessionStorage.getItem("reopenUserInfo") === "true") {
      sessionStorage.removeItem("reopenUserInfo");
      setShowUserInfo(true);
    }
  }, []);

  // Get user avatar from profile - single source of truth
  useEffect(() => {
    // Always fetch fresh from storage
    const currentProfile = getCurrentProfile();
    const avatar = getProfileAvatar(currentProfile);

    setUserAvatar(avatar);
  }, []);

  // Listen for profile updates from other components
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const updatedProfile = event.detail?.profile;

      if (updatedProfile) {
        // Use utility function for single source of truth
        const avatar = getProfileAvatar(updatedProfile);

        setUserAvatar(avatar);
      }
    };

    window.addEventListener("profileUpdated", handleProfileUpdate);

    return () => {
      window.removeEventListener("profileUpdated", handleProfileUpdate);
    };
  }, []);

  // Load Luna AI animation
  useEffect(() => {
    if (isAuthenticated) {
      fetch("/luna_ai.json")
        .then((res) => res.json())
        .then((data) => setLunaAnimation(data))
        .catch((err) => console.error("Failed to load Luna animation:", err));
    }
  }, [isAuthenticated]);

  // Handle sidebar hover - Always check relative to viewport edge
  // Disable on touch devices to prevent scrolling issues
  useEffect(() => {
    // Check if device has touch capability
    const isTouchDevice =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0;

    // Only add mousemove listener on non-touch devices
    if (isTouchDevice) {
      return;
    }

    const handleMouseMove = (e) => {
      // Check if mouse is within 80px of the left edge of viewport
      if (e.clientX <= 80) {
        setSidebarHovered(true);
      } else if (e.clientX > 288) {
        // Close sidebar if mouse moves beyond sidebar width
        setSidebarHovered(false);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Sidebar menu items with outline (light) and filled (solid) icons - matching Android app
  const allMenuItems = [
    {
      iconOutline: "fa-light fa-magnifying-glass",
      iconFilled: "fa-solid fa-magnifying-glass",
      label: "Search",
      id: "search",
      requiresAuth: false,
    },
    {
      iconOutline: "fa-light fa-compass",
      iconFilled: "fa-solid fa-compass",
      label: "Discover",
      id: "discover",
      requiresAuth: false,
      guestOnly: true,
    },
    {
      iconOutline: "fa-light fa-house-blank",
      iconFilled: "fa-solid fa-house-blank",
      label: "Home",
      id: "home",
      requiresAuth: false,
    },
    {
      iconOutline: "fa-light fa-star",
      iconFilled: "fa-solid fa-star",
      label: "Favorites",
      id: "watchlist",
      requiresAuth: true,
    },
    {
      iconOutline: "fa-light fa-bolt",
      iconFilled: "fa-solid fa-bolt",
      label: "Live TV",
      id: "live",
      requiresAuth: true,
    },
    {
      iconOutline: "sports",
      iconFilled: "sports",
      label: "Sports",
      id: "sports",
      isCustomIcon: true,
      requiresAuth: true,
    },
    {
      iconOutline: "fa-light fa-film",
      iconFilled: "fa-solid fa-film",
      label: "Movies",
      id: "movies",
      requiresAuth: false,
    },
    {
      iconOutline: "fa-light fa-tv",
      iconFilled: "fa-solid fa-tv",
      label: "Series",
      id: "series",
      requiresAuth: false,
    },
  ];

  // Filter menu items based on authentication status
  const menuItems = isAuthenticated
    ? allMenuItems.filter((item) => !item.guestOnly)
    : allMenuItems.filter((item) => !item.requiresAuth);

  const allBottomMenuItems = [
    {
      icon: "fa-solid fa-gear",
      iconOutline: "fa-light fa-gear",
      label: "Settings",
      id: "settings",
      requiresAuth: true,
    },
    {
      icon: "fa-solid fa-right-from-bracket",
      iconOutline: "fa-light fa-right-from-bracket",
      label: "Logout",
      id: "logout",
      requiresAuth: true,
    },
    {
      icon: "fa-solid fa-right-to-bracket",
      iconOutline: "fa-light fa-right-to-bracket",
      label: "Login",
      id: "login",
      requiresAuth: false,
    },
  ];

  // Filter bottom menu items based on authentication status
  const bottomMenuItems = isAuthenticated
    ? allBottomMenuItems.filter((item) => item.id !== "login")
    : allBottomMenuItems.filter((item) => !item.requiresAuth);

  const isLit = (id) => activeItem === id || hoveredMenuItem === id;

  const iconOpacity = (id) => {
    if (isLit(id)) return "opacity-100";
    if (!isDarkMode) return "opacity-60";

    return sidebarHovered ? "opacity-70" : "opacity-25";
  };

  const iconColor = (id, isExit = false) => {
    if (!isDarkMode) return "#374151";

    return isLit(id) && !isExit ? "#6495ED" : "#FFFFFF";
  };

  const handleMenuClick = (menuId) => {
    setActiveItem(menuId);

    switch (menuId) {
      case "movies":
        navigate("/web-player/movies", {
          state: { playlist, profile, hasData: true },
        });
        break;
      case "series":
        navigate("/web-player/series", {
          state: { playlist, profile, hasData: true },
        });
        break;
      case "live":
        navigate("/web-player/live-tv", {
          state: { playlist, profile, hasData: true },
        });
        break;
      case "search":
        navigate("/web-player/search", {
          state: { playlist, profile, hasData: true },
        });
        break;
      case "discover":
        navigate("/web-player/discover", {
          state: { playlist, profile, hasData: true },
        });
        break;
      case "watchlist":
        navigate("/web-player/watchlist", {
          state: { playlist, profile, hasData: true },
        });
        break;
      case "settings":
        navigate("/web-player/settings", {
          state: { playlist, profile, hasData: true },
        });
        break;
      case "home":
        navigate("/web-player/home", {
          state: { playlist, profile, hasData: true },
        });
        break;
      case "sports":
        navigate("/web-player/sports", {
          state: { playlist, profile, hasData: true },
        });
        break;
      case "logout":
        // Show logout confirmation modal
        setShowLogoutConfirm(true);
        break;
      case "login":
        navigate("/web-player/login");
        break;
      default:
        break;
    }
  };

  const handleOpenUserInfo = () => {
    setOverlayKey((prev) => prev + 1);
    setShowUserInfo(true);
  };

  const handleLogout = () => {
    // Clear all session data
    webPlayerStorage.clearAll();
    // Navigate to home page
    navigate("/web-player/home");
    // Reload to reset state
    window.location.reload();
  };

  // Handle Luna AI commands
  const handleLunaCommand = (command) => {
    if (!command) return;

    switch (command.type) {
      case "navigate":
        const target = command.target?.toLowerCase();

        if (target === "home")
          navigate("/web-player/home", {
            state: { playlist, profile, hasData: true },
          });
        else if (target === "movies")
          navigate("/web-player/movies", {
            state: { playlist, profile, hasData: true },
          });
        else if (target === "series")
          navigate("/web-player/series", {
            state: { playlist, profile, hasData: true },
          });
        else if (target === "live_tv")
          navigate("/web-player/live-tv", {
            state: { playlist, profile, hasData: true },
          });
        else if (target === "sports")
          navigate("/web-player/sports", {
            state: { playlist, profile, hasData: true },
          });
        else if (target === "search")
          navigate("/web-player/search", {
            state: { playlist, profile, hasData: true },
          });
        else if (target === "favorites" || target === "watchlist")
          navigate("/web-player/watchlist", {
            state: { playlist, profile, hasData: true },
          });
        else if (target === "settings")
          navigate("/web-player/settings", {
            state: { playlist, profile, hasData: true },
          });
        break;
      case "search":
        navigate("/web-player/search", {
          state: { playlist, profile, hasData: true, query: command.query },
        });
        break;
      case "play":
        // Handle play commands based on target
        if (command.params?.mediaId) {
          if (command.target === "movie") {
            navigate(`/web-player/movies/${command.params.mediaId}`, {
              state: { playlist, profile, hasData: true },
            });
          } else if (command.target === "series") {
            navigate(`/web-player/series/${command.params.mediaId}`, {
              state: { playlist, profile, hasData: true },
            });
          }
        }
        break;
      default:
        break;
    }
  };

  return (
    <>
      {/* User Info Overlay */}
      <UserInfoOverlay
        isOpen={showUserInfo}
        key={overlayKey}
        playlist={playlist}
        profile={profile}
        onClose={() => setShowUserInfo(false)}
      />

      {/* Luna AI Overlay */}
      {isAuthenticated && (
        <LunaAIOverlay
          currentScreen={activeMenuItem}
          isOpen={showLunaAI}
          onClose={() => setShowLunaAI(false)}
          onCommand={handleLunaCommand}
        />
      )}

      {/* Floating icon rail. Visible on every size, and scrollable when the icons do not fit. */}
      <div
        className="lunova-rail fixed top-0 left-0 z-[200] flex h-[100dvh] w-16 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain py-3 pl-2 pointer-events-auto [scrollbar-width:none] lg:w-20 lg:py-8 lg:pl-4 [&::-webkit-scrollbar]:hidden"
        style={{
          transform: "translate3d(0,0,0)",
          WebkitTransform: "translate3d(0,0,0)",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          margin: 0,
        }}
      >
        {/* Profile Icon at top - Show user avatar when authenticated, Lunova logo when logged out */}
        <button
          className={`rounded-full overflow-hidden flex items-center justify-center shadow-lg opacity-100 mb-6 hover:scale-110 hover:ring-4 hover:ring-[#6495ED] transition-all duration-300 cursor-pointer flex-shrink-0 pointer-events-auto ${
            isAuthenticated
              ? "w-11 h-11 bg-gradient-to-br from-purple-500 to-pink-500"
              : "w-8 h-8 bg-gray-800"
          }`}
          onClick={
            isAuthenticated
              ? handleOpenUserInfo
              : () => navigate("/web-player/login")
          }
        >
          {isAuthenticated ? (
            userAvatar ? (
              <img
                alt={profile?.name || "Profile"}
                className="w-full h-full object-cover"
                src={userAvatar}
                onError={(e) => {
                  e.target.src = "/default-avatar.png";
                }}
              />
            ) : (
              <i className="fas fa-user text-white text-lg" />
            )
          ) : (
            <img
              alt="Lunova"
              className="w-full h-full object-cover"
              src="/logo.png"
            />
          )}
        </button>

        {/* Luna AI Button - Hidden for now */}
        {false && isAuthenticated && lunaAnimation && (
          <button
            className={`w-12 h-12 rounded-full flex items-center justify-center mb-6 transition-all duration-300 pointer-events-auto hover:scale-110 ${
              isLunaHovered ? "opacity-100" : "opacity-60"
            }`}
            onClick={() => setShowLunaAI(true)}
            onMouseEnter={() => setIsLunaHovered(true)}
            onMouseLeave={() => setIsLunaHovered(false)}
          >
            <Lottie
              animationData={lunaAnimation}
              className="w-10 h-10"
              loop={isLunaHovered}
            />
          </button>
        )}

        {/* Menu Icons */}
        <div className="my-auto flex w-full shrink-0 flex-col gap-3 py-3 lg:gap-6 lg:py-4">
          {menuItems.map((item) => (
            <button
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 pointer-events-auto ${
                hoveredMenuItem === item.id
                  ? isDarkMode
                    ? "bg-white/10"
                    : "bg-gray-900/10"
                  : ""
              } ${iconOpacity(item.id)}`}
              key={item.id}
              style={{ color: iconColor(item.id) }}
              onClick={() => handleMenuClick(item.id)}
              onMouseEnter={() => setHoveredMenuItem(item.id)}
              onMouseLeave={() => setHoveredMenuItem(null)}
            >
              {item.isCustomIcon ? (
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 640 640"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M144 176C144 114.1 194.1 64 256 64L272 64C280.8 64 288 71.2 288 80L288 96C288 156.2 240.5 205.3 181 207.9C157.1 240.1 144 279.4 144 320C144 417.2 222.8 496 320 496L320 496C388.7 496 441.4 518.7 461.3 532C472.3 539.4 475.3 554.3 468 565.3C460.7 576.3 445.7 579.3 434.7 572C422.6 564 379.3 544 320 544C260.7 544 217.4 563.9 205.3 572C194.3 579.4 179.4 576.4 172 565.3C164.6 554.2 167.6 539.4 178.7 532C185.7 527.3 196.8 521.5 211.2 515.8C204.7 512.2 198.4 508.2 192.3 504C132.7 532.2 60.4 510.3 26.9 452.2L18.9 438.3C14.5 430.6 17.1 420.9 24.8 416.4C38.6 408.4 53.2 403.3 68.1 400.9C27.7 379.4 .1 336.8 .1 287.8L.1 271.8C.1 263 7.3 255.8 16.1 255.8C32 255.8 47.2 258.7 61.3 264C37 225.2 34.5 174.5 59 132.1L67 118.2C71.4 110.5 81.2 107.9 88.9 112.3C115.1 127.4 134 150.5 144.2 176.7L144.2 175.7zM384 64C445.9 64 496 114.1 496 176L496 177C506.2 150.8 525 127.8 551.3 112.6C559 108.2 568.7 110.8 573.2 118.5L581.2 132.4C605.7 174.8 603.1 225.5 578.9 264.3C593 259 608.2 256.1 624.1 256.1C632.9 256.1 640.1 263.3 640.1 272.1L640.1 288.1C640.1 337.1 612.6 379.7 572.1 401.2C586.9 403.6 601.6 408.7 615.3 416.7C623 421.1 625.6 430.9 621.2 438.6L613.2 452.5C591 491 551.7 513.6 510.5 516.3C505.1 507 497.6 498.7 488 492.4C474.1 483.1 451.5 472 422.7 463.3C467.1 431.4 496.1 379.2 496.1 320.3C496.1 279.7 483 240.4 459.1 208.2C399.6 205.6 352.1 156.5 352.1 96.3L352.1 80.3C352.1 71.5 359.3 64.3 368.1 64.3L384.1 64.3z" />
                </svg>
              ) : (
                <i
                  className={`${
                    activeItem === item.id
                      ? item.iconFilled
                      : hoveredMenuItem === item.id
                        ? item.iconFilled
                        : item.iconOutline
                  } text-xl`}
                />
              )}
            </button>
          ))}
        </div>

        {/* Bottom Menu Icons (Settings & Logout) */}
        <div className="flex w-full shrink-0 flex-col gap-3 lg:gap-4">
          {bottomMenuItems.map((item) => (
            <button
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 pointer-events-auto ${
                hoveredMenuItem === item.id
                  ? isDarkMode
                    ? "bg-white/10"
                    : "bg-gray-900/10"
                  : ""
              } ${iconOpacity(item.id)}`}
              key={item.id}
              style={{ color: iconColor(item.id, item.id === "logout") }}
              onClick={() => handleMenuClick(item.id)}
              onMouseEnter={() => setHoveredMenuItem(item.id)}
              onMouseLeave={() => setHoveredMenuItem(null)}
            >
              <i
                className={`${
                  activeItem === item.id
                    ? item.icon
                    : hoveredMenuItem === item.id
                      ? item.icon
                      : item.iconOutline
                } text-xl`}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Sliding Sidebar - Hidden on mobile, visible on desktop */}
      <AnimatePresence>
        {sidebarHovered && (
          <motion.div
            animate={{ x: 0 }}
            className="lunova-glass top-0 left-0 w-72 !border-y-0 !border-l-0 z-[190] hidden lg:flex flex-col !shadow-[inset_-1px_0_0_rgba(255,255,255,0.08),10px_0_30px_rgba(0,0,0,0.5)] overflow-y-auto overflow-x-hidden fixed h-full"
            exit={{ x: -288 }}
            initial={{ x: -288 }}
            ref={sidebarRef}
            style={{
              transform: "translate3d(0,0,0)",
              WebkitTransform: "translate3d(0,0,0)",
              margin: 0,
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
            onMouseEnter={() => setSidebarHovered(true)}
            onMouseLeave={() => setSidebarHovered(false)}
          >
            {/* Profile name at top - Show "Login" when logged out */}
            <button
              className="flex items-center h-11 mt-8 mb-6 pl-4 w-full text-left hover:bg-transparent"
              onClick={
                isAuthenticated
                  ? handleOpenUserInfo
                  : () => navigate("/web-player/login")
              }
            >
              <div className="w-12 h-12 flex items-center justify-center">
                <div className="w-12 h-12" />
              </div>
              <span className="ml-4 text-white font-medium hover:font-bold text-base relative hover:after:content-[''] hover:after:absolute hover:after:bottom-[-5px] hover:after:left-0 hover:after:w-full hover:after:h-[4px] hover:after:bg-white hover:after:opacity-60 cursor-pointer transition-all">
                {isAuthenticated ? profile?.name || "Profile" : "Login"}
              </span>
            </button>

            {/* Luna AI Button in sidebar - Hidden for now */}
            {false && isAuthenticated && lunaAnimation && (
              <button
                className="flex items-center h-12 mb-6 pl-4 w-full text-left hover:bg-transparent group"
                onClick={() => setShowLunaAI(true)}
              >
                <div className="w-12 h-12 flex items-center justify-center">
                  <div className="w-12 h-12" />
                </div>
                <span className="ml-4 text-white/60 font-medium group-hover:text-white group-hover:font-bold text-base relative group-hover:after:content-[''] group-hover:after:absolute group-hover:after:bottom-[-5px] group-hover:after:left-0 group-hover:after:w-full group-hover:after:h-[4px] group-hover:after:bg-white group-hover:after:opacity-60 cursor-pointer transition-all">
                  Luna AI
                </span>
              </button>
            )}

            {/* Menu items aligned with icons */}
            <div className="flex-1 flex flex-col justify-center">
              {menuItems.map((item, index) => (
                <button
                  className={`h-12 flex items-center pl-4 transition-all duration-200 group ${
                    activeItem === item.id
                      ? "text-white opacity-100"
                      : "text-white/60 hover:text-white hover:opacity-100"
                  } ${index < menuItems.length - 1 ? "mb-6" : ""}`}
                  key={item.id}
                  onClick={() => handleMenuClick(item.id)}
                  onMouseEnter={() => setHoveredMenuItem(item.id)}
                  onMouseLeave={() => setHoveredMenuItem(null)}
                >
                  <div className="flex items-center">
                    {/* Placeholder for icon alignment */}
                    <div className="w-12 h-12 flex items-center justify-center">
                      <div className="w-12 h-12" />
                    </div>
                    {/* Text label with spacing */}
                    <span className="font-medium group-hover:font-bold text-base ml-4 relative group-hover:after:content-[''] group-hover:after:absolute group-hover:after:bottom-[-5px] group-hover:after:left-0 group-hover:after:w-full group-hover:after:h-[4px] group-hover:after:opacity-60 group-hover:after:bg-white transition-all">
                      {item.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* Bottom Menu Items (Settings & Logout) */}
            <div className="flex flex-col gap-4 mb-8">
              {bottomMenuItems.map((item) => (
                <button
                  className={`h-12 flex items-center pl-4 transition-all duration-200 group ${
                    activeItem === item.id
                      ? "text-white opacity-100"
                      : item.id === "logout"
                        ? "text-red-500 hover:text-red-400 hover:opacity-100"
                        : "text-white/60 hover:text-white hover:opacity-100"
                  }`}
                  key={item.id}
                  onClick={() => handleMenuClick(item.id)}
                  onMouseEnter={() => setHoveredMenuItem(item.id)}
                  onMouseLeave={() => setHoveredMenuItem(null)}
                >
                  <div className="flex items-center">
                    {/* Placeholder for icon alignment */}
                    <div className="w-12 h-12 flex items-center justify-center">
                      <div className="w-12 h-12" />
                    </div>
                    {/* Text label with spacing */}
                    <span className="font-medium group-hover:font-bold text-base ml-4 relative group-hover:after:content-[''] group-hover:after:absolute group-hover:after:bottom-[-5px] group-hover:after:left-0 group-hover:after:w-full group-hover:after:h-[4px] group-hover:after:bg-current group-hover:after:opacity-60 transition-all">
                      {item.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Logout Confirmation Modal */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[100] flex items-center justify-center"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              role="presentation"
              onClick={() => setShowLogoutConfirm(false)}
            />

            {/* Modal */}
            <motion.div
              animate={{ scale: 1, opacity: 1 }}
              className="relative z-10 bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4 border border-gray-700"
              exit={{ scale: 0.95, opacity: 0 }}
              initial={{ scale: 0.95, opacity: 0 }}
            >
              <h2 className="text-xl font-bold text-white mb-3">
                Confirm Logout
              </h2>
              <p className="text-gray-300 mb-6">
                Are you sure you want to log out? You will be returned to the
                home screen and will need to log in again to access your
                account.
              </p>

              <div className="flex gap-3">
                <button
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium transition-colors"
                  onClick={() => setShowLogoutConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LeftMenu;
